import { Camera, Engine, Scene, TransformNode } from "babylonjs";
import ContentBase from "../../content/core/content.base";
import CreatorGrid from "./creator.grid";
import CreatorElement, { convertPosition, convertScale, rotationToQuaternion } from "./creator.element";
import CreatorImageElement from "./creator.image";
import CreatorVideoElement from "./creator.video";
import CreatorGlbElement from "./creator.glb";
import { disposeModelCache } from "../loader/loader.modelinstance";
import VideoPool, { VideoPoolConfig } from "../video/video.pool";
import {
  CreatorArtworkWorld,
  CreatorFile,
  CreatorFileGlbType,
  CreatorFileVideoAlphaType,
  CreatorFileVideoType,
  CreatorSceneAsset,
  CreatorSceneChild,
  CreatorSceneConfig,
} from "./types";

function basename(path: string): string {
  try {
    return new URL(path).pathname.split("/").pop() ?? path;
  } catch {
    return path.split("/").pop() ?? path;
  }
}

function isVideoFile(file: CreatorFile): boolean {
  return file.type === CreatorFileVideoType || file.type === CreatorFileVideoAlphaType;
}

// Construct the element for a given file type. Falls back to image. Video panels
// receive a pooled <video> element from getVideoElement() (if a pool exists).
function createCreatorElement(
  scene: Scene,
  parent: TransformNode,
  child: CreatorSceneChild,
  file: CreatorFile,
  getVideoElement?: () => HTMLVideoElement | undefined,
): CreatorElement {
  switch (file.type) {
    case CreatorFileGlbType:
      return new CreatorGlbElement(scene, parent, child, file);
    case CreatorFileVideoType:
      return new CreatorVideoElement(scene, parent, child, file, false, getVideoElement?.());
    case CreatorFileVideoAlphaType:
      return new CreatorVideoElement(scene, parent, child, file, true, getVideoElement?.());
    default:
      return new CreatorImageElement(scene, parent, child, file);
  }
}

//----------------------------------------------------------------
export interface CreatorContentConfig {
  // show the reference floor grid. OFF by default (e.g. XR, where the real world
  // is the reference); turn ON for flat viewers/editor where a ground reference
  // helps read the content's position/animation.
  showGrid?: boolean;
  // apply the sceneConfig scene-root transform to the content node (editor/demo
  // parity). Default true; AR apps set false (the anchor places the content).
  applyRootTransform?: boolean;
  // DOM parent for the pooled <video> elements (default document.body).
  videoParent?: HTMLElement;
  // initial mute state for the VideoPool.
  muted?: boolean;
}

//----------------------------------------------------------------
// renders a creator artwork (files + sceneConfig) as a registry of elements
// (image/video/glb), each owning an anchor node, under a single content node.
// Loaded via AppContent.setContent (init) and torn down via dispose. Exposes its
// node + elements + pooled video elements so a host (the editor's gizmos, or an
// AR app's placement/reveal) can drive the content in bespoke ways.
export default class CreatorContent extends ContentBase {
  protected artwork: CreatorArtworkWorld;
  protected config: CreatorContentConfig;
  protected node?: TransformNode;
  protected grid?: CreatorGrid;
  protected elements: CreatorElement[] = [];
  // Centralised <video> elements for the video panels (gesture-unlock + mute).
  protected videoPool?: VideoPool;

  // True once init/build has finished. Hosts gate reveal/placement on this.
  public isLoaded = false;

  constructor(artwork: CreatorArtworkWorld, config: CreatorContentConfig = {}) {
    super();
    this.artwork = artwork;
    this.config = config;
  }

  public override async init(engine: Engine, scene: Scene, camera: Camera, rootNode?: TransformNode): Promise<void> {
    super.init(engine, scene, camera, rootNode);
    this.node = new TransformNode("creator", this.scene!);
    this.node.parent = this.rootNode ?? null;

    // Apply the scene-root transform (editor/demo parity). AR placement sets
    // applyRootTransform=false — the anchor positions the content instead.
    const root = this.artwork.sceneConfig?.scenes?.[0];
    if (this.config.applyRootTransform !== false && root) {
      this.node.position = convertPosition(root.position);
      this.node.rotationQuaternion = rotationToQuaternion(root.rotation, false); // a container, not a panel
      this.node.scaling = convertScale(root.scale);
    }

    if (this.config.showGrid) {
      this.grid = new CreatorGrid(this.scene!);
    }
    await this.build();
    this.isLoaded = true;
  }

  protected async build(): Promise<void> {
    const config = this.artwork.sceneConfig;
    if (!config) return;
    const fileByAssetId = this.mapFilesToAssetIds(this.artwork.files ?? [], config.assets ?? []);
    const root = config.scenes?.[0];
    const children = root?.children ?? [];

    // Create a VideoPool sized to the number of video panels — one pooled <video>
    // per video element (gesture-unlock for mobile autoplay + centralised mute).
    const videoCount = children.filter(child => {
      const file = fileByAssetId.get(child.assetID);
      return !!file && isVideoFile(file);
    }).length;
    if (videoCount > 0) {
      const poolConfig = new VideoPoolConfig();
      poolConfig.numOfVideos = videoCount;
      poolConfig.parentElement = this.config.videoParent;
      poolConfig.muted = this.config.muted ?? false;
      this.videoPool = new VideoPool(poolConfig);
    }

    // build all elements in parallel; one failure shouldn't block the others.
    await Promise.all(children.map(child => {
      const file = fileByAssetId.get(child.assetID);
      if (!file) return Promise.resolve(undefined);
      return this.addElement(child, file).catch(err => {
        console.error("creator.content - failed to build element:", child.sceneID, err);
        if (err?.stack) console.error("creator.content - stack:", err.stack);
        return undefined;
      });
    }));
  }

  // create + build + register one element. Used by build() in a batch, and
  // available for the editor to add an element live.
  public async addElement(child: CreatorSceneChild, file: CreatorFile): Promise<CreatorElement> {
    const element = createCreatorElement(
      this.scene!, this.node!, child, file, () => this.videoPool?.getVideoByNextIndex(),
    );
    this.elements.push(element);
    await element.build();
    element.stampMetadata();
    return element;
  }

  public removeElement(sceneID: string): void {
    const idx = this.elements.findIndex(e => e.sceneID === sceneID);
    if (idx < 0) return;
    this.elements[idx].dispose();
    this.elements.splice(idx, 1);
  }

  public getElement(sceneID: string): CreatorElement | undefined {
    return this.elements.find(e => e.sceneID === sceneID);
  }

  //---------------------------------------------------------- host accessors.
  // The content's root transform node (host reparents this under an AR anchor).
  public get contentNode(): TransformNode | undefined {
    return this.node;
  }

  // The element registry (host reveals / measures via element.getMeshes()).
  public get contentElements(): readonly CreatorElement[] {
    return this.elements;
  }

  // The pooled <video> elements (host waits on readyState for first-frame fades).
  public getVideoElements(): HTMLVideoElement[] {
    return this.videoPool?.getVideos() ?? [];
  }

  public setMuted(muted: boolean): void {
    this.videoPool?.setMuted(muted);
  }

  public getMuted(): boolean {
    return this.videoPool?.getMuted() ?? false;
  }

  // Show/hide the whole content (toggles the node + each element, e.g. video
  // pauses when hidden).
  public setEnabled(visible: boolean): void {
    this.node?.setEnabled(visible);
    for (const element of this.elements) element.setEnabled(visible);
  }

  // Serialize the live element transforms back to a creator (Unity-space)
  // sceneConfig — the editor's save path. Round-trips the input for an unedited
  // scene; reflects gizmo edits once applied to the anchors.
  public getSceneConfig(): CreatorSceneConfig {
    const source = this.artwork.sceneConfig;
    const root = source?.scenes?.[0];
    const children: CreatorSceneChild[] = this.elements.map(el => {
      const t = el.readTransform();
      return {
        sceneID: el.sceneID,
        assetID: el.assetId,
        position: t.position,
        rotation: t.rotation,
        scale: t.scale,
      };
    });
    return {
      version: source?.version ?? 1,
      assets: source?.assets ?? [],
      scenes: [
        {
          sceneID: root?.sceneID ?? "root",
          position: root?.position ?? [0, 0, 0],
          rotation: root?.rotation ?? [0, 0, 0],
          scale: root?.scale ?? [1, 1, 1],
          children,
        },
      ],
    };
  }

  // match each scene child's assetID to a file: by file.id, else by the asset's
  // path (filename) via the sceneConfig assets table.
  protected mapFilesToAssetIds(files: CreatorFile[], assets: CreatorSceneAsset[]): Map<string, CreatorFile> {
    const byId = new Map<string, CreatorFile>();
    const byName = new Map<string, CreatorFile>();
    for (const file of files) {
      if (file.id) byId.set(file.id, file);
      byName.set(basename(file.path), file);
    }
    for (const asset of assets) {
      if (byId.has(asset.assetID)) continue;
      const file = byName.get(asset.assetPath);
      if (file) byId.set(asset.assetID, file);
    }
    return byId;
  }

  public override dispose(): void {
    for (const element of this.elements) element.dispose();
    this.elements = [];

    // dispose the shared model containers AFTER their instances are gone.
    if (this.scene) disposeModelCache(this.scene);

    this.videoPool?.dispose();
    this.videoPool = undefined;

    this.grid?.dispose();
    this.grid = undefined;

    this.node?.dispose(false, true);
    this.node = undefined;
    this.isLoaded = false;
  }
}
